import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts';
import { PieChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import {
  Sec,
  SecTitle,
  Box,
  ReportTable,
  Split,
  PieWrap,
  Foot,
} from '../styled';
import LoadingML from '@/components/LoadingML';
import { fmtThousand } from '@/utils/comUtil';

echarts.use([TooltipComponent, LegendComponent, CanvasRenderer, PieChart]);

const PIE_PALETTE = ['#1565c0', '#42a5f5', '#90caf9'];

// 售票渠道拆分：按 demo-开发用.html 视觉
//   标题 + 左表（5 列）+ 右 donut + 脚注
// 数据：state.performSales.channelSplit
const ChannelSplit = ({ data, loading }) => {
  const { rows = [] } = data || {};

  const num = (v) => fmtThousand(v, 0);
  // 金额：原值即元，转为「¥X.X 万」
  const amt = (yuan) => {
    if (!yuan) {
      return '¥0';
    }
    const wan = 10000;
    if (yuan < wan) {
      return `¥${num(yuan)}`;
    }
    const val = Number(yuan) / wan;
    return `¥${fmtThousand(val, 1)} 万`;
  };

  const pieOption = useMemo(() => {
    const pieData = rows.map((r, i) => ({
      name: r.channel,
      value: r.tickets,
      itemStyle: { color: PIE_PALETTE[i] || '#90caf9' },
    }));
    return {
      tooltip: { trigger: 'item', confine: true },
      legend: {
        show: true,
        orient: 'vertical',
        right: 24,
        top: 'middle',
        icon: 'roundRect',
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 14,
        padding: 0,
        textStyle: { color: '#333', fontSize: 12 },
        data: pieData.map((d) => d.name),
        formatter: (name) => {
          const item = pieData.find((d) => d.name === name);
          const total = pieData.reduce((s, d) => s + d.value, 0) || 1;
          const pct = item ? ((item.value / total) * 100).toFixed(1) : '0.0';
          return `${name}  ${pct}%`;
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['46%', '80%'],
          center: ['30%', '50%'],
          avoidLabelOverlap: false,
          label: { show: false },
          labelLine: { show: false },
          data: pieData,
        },
      ],
    };
  }, [rows]);

  const total = rows.reduce((s, r) => s + r.tickets, 0);

  return (
    <Sec>
      <SecTitle>
        售票渠道拆分<em>文广票池剧目</em>
      </SecTitle>
      <Box>
        <LoadingML loading={loading ? 1 : 0}>
          <Split>
            <ReportTable>
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>售票渠道</th>
                  <th style={{ width: '18%' }}>已售张数</th>
                  <th style={{ width: '20%' }}>实付金额</th>
                  <th style={{ width: '14%' }}>张数占比</th>
                  <th>数据来源 / 时效</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const pct = total
                    ? `${((r.tickets / total) * 100).toFixed(1)}%`
                    : '—';
                  return (
                    <tr key={r.channel}>
                      <td>{r.channel}</td>
                      <td>{num(r.tickets)}</td>
                      <td>{amt(r.amount)}</td>
                      <td>{pct}</td>
                      <td style={{ textAlign: 'left' }}>{r.source}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td>合计</td>
                  <td>{num(total)}</td>
                  <td>{amt(rows.reduce((s, r) => s + r.amount, 0))}</td>
                  <td>100%</td>
                  <td style={{ textAlign: 'left' }}>—</td>
                </tr>
              </tbody>
            </ReportTable>

            <PieWrap>
              <div style={{ width: 320, height: 140 }}>
                <ReactEChartsCore
                  echarts={echarts}
                  option={pieOption}
                  notMerge
                  lazyUpdate
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </PieWrap>
          </Split>

          <Foot>本表仅对文广票池剧目展示。非文广票池不展示本表</Foot>
        </LoadingML>
      </Box>
    </Sec>
  );
};

export default ChannelSplit;
